'use client';

import { useEffect, useState } from 'react';

export type ImpersonationEligibilityReason =
  | 'missing-email'
  | 'no-matching-portal-user'
  | 'duplicate-portal-users'
  | 'email-not-verified'
  | 'not-client-role';

export type ImpersonationEligibilityCustomer = {
  id?: number;
  emailAddress?: string | null;
};

export type SafeImpersonationEligibilityCustomer = {
  id: number;
  emailAddress?: string | null;
};

export type ImpersonationEligibilityResult =
  | { customerId: number; eligible: true }
  | { customerId: number; eligible: false; reason: ImpersonationEligibilityReason };

export type ImpersonationEligibilityState = {
  isLoading: boolean;
  byCustomerId: Record<number, ImpersonationEligibilityResult>;
};

export type GetImpersonationEligibility = (
  customers: SafeImpersonationEligibilityCustomer[],
) => Promise<ImpersonationEligibilityResult[]>;

export const INELIGIBLE_IMPERSONATION_LABEL =
  'Customer portal login not ready. Ask them to sign up or sign in with the same email, verify their email, and confirm they are a Client user.';

export const CHECKING_IMPERSONATION_ELIGIBILITY_LABEL = 'Checking customer portal login...';

export const INVALID_CUSTOMER_ID_IMPERSONATION_LABEL = 'Customer ID is invalid for impersonation.';

export function hasSafeCustomerId<T extends { id?: number }>(
  customer: T,
): customer is T & { id: number } {
  return typeof customer.id === 'number' && Number.isSafeInteger(customer.id);
}

export function getEligibleImpersonationCustomers(
  customers: ImpersonationEligibilityCustomer[],
): SafeImpersonationEligibilityCustomer[] {
  return customers
    .filter(hasSafeCustomerId)
    .map((customer) => ({ id: customer.id, emailAddress: customer.emailAddress }));
}

export const mapEligibilityByCustomerId = (results: ImpersonationEligibilityResult[]) => {
  return Object.fromEntries(results.map((result) => [result.customerId, result]));
};

export function parseEligibilityResults(results: unknown[]): ImpersonationEligibilityResult[] {
  return results.filter((result): result is ImpersonationEligibilityResult => {
    if (!isRecord(result) || typeof result.customerId !== 'number') {
      return false;
    }

    if (result.eligible === true) {
      return true;
    }

    return result.eligible === false && isImpersonationEligibilityReason(result.reason);
  });
}

export function useImpersonationEligibility(
  customers: ImpersonationEligibilityCustomer[],
  getEligibility?: GetImpersonationEligibility,
): ImpersonationEligibilityState {
  const [state, setState] = useState<ImpersonationEligibilityState>({
    isLoading: false,
    byCustomerId: {},
  });

  const eligibilityRequestKey = JSON.stringify(getEligibleImpersonationCustomers(customers));

  useEffect(() => {
    let ignore = false;
    const eligibleCustomers = JSON.parse(
      eligibilityRequestKey,
    ) as SafeImpersonationEligibilityCustomer[];

    if (!getEligibility || eligibleCustomers.length === 0) {
      setState({ isLoading: false, byCustomerId: {} });
      return () => {
        ignore = true;
      };
    }

    setState({ isLoading: true, byCustomerId: {} });
    getEligibility(eligibleCustomers)
      .then((results) => {
        if (ignore) return;
        setState({ isLoading: false, byCustomerId: mapEligibilityByCustomerId(results) });
      })
      .catch(() => {
        if (ignore) return;
        setState({ isLoading: false, byCustomerId: {} });
      });

    return () => {
      ignore = true;
    };
  }, [eligibilityRequestKey, getEligibility]);

  return state;
}

const isImpersonationEligibilityReason = (
  reason: unknown,
): reason is ImpersonationEligibilityReason => {
  return (
    reason === 'missing-email' ||
    reason === 'no-matching-portal-user' ||
    reason === 'duplicate-portal-users' ||
    reason === 'email-not-verified' ||
    reason === 'not-client-role'
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};
